// Loaded only on the under-$2,000 buyer guide. Pure calculations use integer cents.
// No network requests, storage, analytics events, URL changes or cart actions.
const labels = { budget: 'spending limit', ring: 'ring price', band: 'wedding band price', tax: 'tax amount' };
export function parseAmount(raw, optional = false) {
  const text = String(raw ?? '').trim();
  if (!text && optional) return { valid: true, cents: null };
  if (!/^\$?(?:\d{1,9}|\d{1,3}(?:,\d{3}){1,2})(?:\.\d{1,2})?$/.test(text)) return { valid: false };
  const [whole, fraction = ''] = text.replace(/[$,]/g, '').split('.');
  const cents = Number(whole) * 100 + Number(fraction.padEnd(2, '0'));
  return { valid: Number.isSafeInteger(cents) && cents >= 0, cents };
}
export function calculateBudget(values) {
  const amounts = {}, errors = {};
  for (const field of ['budget', 'ring', 'band', 'tax']) {
    const parsed = parseAmount(values[field], field === 'tax');
    if (!parsed.valid) errors[field] = 'Enter a nonnegative ' + labels[field] + ' using up to two decimal places (for example, 1800.50). Maximum: 999,999,999.99.';
    else amounts[field] = parsed.cents;
  }
  if (!['0', '300'].includes(String(values.rush))) errors.rush = 'Choose Standard shipping or optional Rush.';
  if (Object.keys(errors).length) return { valid: false, errors };
  const subtotal = amounts.ring + amounts.band + Number(values.rush) * 100;
  const taxKnown = amounts.tax !== null, total = subtotal + (amounts.tax ?? 0);
  return { valid: true, subtotal, taxKnown, tax: amounts.tax, total, remaining: amounts.budget - total };
}
export function formatMoney(cents) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(cents / 100);
}
function initialize(root) {
  if (root.dataset.budgetReady === 'true') return;
  const fields = Object.fromEntries([...root.querySelectorAll('[data-budget-field]')].map(e => [e.dataset.budgetField, e]));
  const status = root.querySelector('[data-budget-status]'), results = root.querySelector('[data-budget-results]');
  const button = root.querySelector('[data-budget-calculate]');
  const totalLabel = root.querySelector('[data-budget-total-label]'), totalValue = root.querySelector('[data-budget-total]');
  const remainingLabelElement = root.querySelector('[data-budget-remaining-label]'), remainingValue = root.querySelector('[data-budget-remaining]');
  if (!['budget', 'ring', 'band', 'rush', 'tax'].every(key => fields[key]) ||
      !status || !results || !button || !totalLabel || !totalValue || !remainingLabelElement || !remainingValue) return;
  root.dataset.budgetReady = 'true';
  function calculate(focusError = false) {
    const result = calculateBudget(Object.fromEntries(Object.entries(fields).map(([key, e]) => [key, e.value])));
    for (const [key, e] of Object.entries(fields)) {
      const error = root.querySelector('[data-budget-error="' + key + '"]'), message = result.errors?.[key] || '';
      e.setAttribute('aria-invalid', message ? 'true' : 'false');
      if (error) { error.textContent = message; error.hidden = !message; }
    }
    if (!result.valid) {
      results.hidden = true;
      status.textContent = 'Check the marked amounts, then calculate again.';
      if (focusError) fields[Object.keys(result.errors)[0]]?.focus();
      return;
    }
    totalLabel.textContent = result.taxKnown ? 'Planned total with your tax amount' : 'Subtotal before tax';
    totalValue.textContent = formatMoney(result.total);
    const remainingLabel = result.remaining < 0 ? 'Over budget' : 'Budget remaining';
    remainingLabelElement.textContent = remainingLabel + (result.taxKnown ? ' with your tax amount' : ' before tax');
    remainingValue.textContent = formatMoney(Math.abs(result.remaining));
    results.dataset.overBudget = result.remaining < 0 ? 'true' : 'false';
    results.hidden = false;
    status.textContent = result.taxKnown
      ? 'This plan uses the tax amount you entered. Confirm the actual total at checkout.'
      : 'Tax has not been entered. This does not confirm whether the final checkout total fits your budget.';
  }
  button.addEventListener('click', () => calculate(true));
  root.addEventListener('keydown', event => {
    if (event.key === 'Enter' && event.target.matches('input[data-budget-field]')) { event.preventDefault(); calculate(true); }
  });
  for (const field of Object.values(fields)) field.addEventListener(field.tagName === 'SELECT' ? 'change' : 'input', () => {
    results.hidden = true;
    status.textContent = 'Values changed. Select Calculate my budget to update your plan.';
  });
  root.hidden = false;
  calculate();
}
if (typeof document !== 'undefined' && location.pathname === '/blogs/news/lab-grown-engagement-rings-under-2000') {
  for (const root of document.querySelectorAll('[data-bey-budget-planner]')) initialize(root);
}


